// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Master API client (Stage 6A.2).
//
// Thin axios wrappers around `/api/erp/masters/customers/*`. Reuses
// the shared tenant axios client (`api/client.js`) so the auth
// header + refresh-token interceptor are inherited unchanged.
//
// The list endpoint goes through POST `/list` so the full
// GridQueryState (including nested `advancedFilters`) survives the
// network — query strings cannot represent deep objects.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

const BASE = '/api/erp/masters/customers';

// ─────────────────────────────────────────────────────────────────
// Stage 6A.2K-fix — Frontend → Backend field-name bridge
//
// The reusable ErpDataGrid sends column `dataIndex` / `key` verbatim
// in `filters[].field`, `sort[].field`, and `groupBy` — all of which
// are camelCase in our column definitions (`billingState`,
// `customerCategory`, `priceGroup`, …). The backend gridQueryParser
// and `GRID_GROUPABLE_FIELDS` whitelist use snake_case keys
// (`billing_state`, `customer_category`, `price_group`, …) so the
// allow-list lookup silently dropped every filter / sort / groupBy
// the grid emitted → filtering and grouping appeared to do nothing.
//
// This map is the bridge. Every key the customer grid can emit lands
// here paired with the snake_case SQL field name on the backend
// (mirroring `backend/src/services/erp/customers/customer.constants.js`
// → GRID_ALLOWED_FIELDS / GRID_GROUPABLE_FIELDS).
// ─────────────────────────────────────────────────────────────────
const GRID_FIELD_TO_SQL = {
  customerCode:           'customer_code',
  customerName:           'customer_name',
  displayName:            'display_name',
  customerCategory:       'customer_category',
  priceGroup:             'price_group',
  status:                 'status',
  isActive:               'is_active',
  isSez:                  'is_sez',
  isCompositionDealer:    'is_composition_dealer',
  customerSource:         'customer_source',
  customerStatusAxis:     'customer_status_axis',
  mobileVerified:         'mobile_verified',
  portalRegisteredAt:     'portal_registered_at',
  salesRepresentativeId:  'sales_representative_id',
  salesRepresentativeName: 'sales_representative_id',
  kycVerificationStatus:  'kyc_verification_status',
  parentCustomerId:       'parent_customer_id',
  gstRegistrationType:    'gst_registration_type',
  gstin:                  'gstin',
  pan:                    'pan',
  gstStateCode:           'gst_state_code',
  gstVerificationStatus:  'gst_verification_status',
  mobile:                 'mobile',
  email:                  'email',
  billingCity:            'billing_city',
  billingState:           'billing_state',
  creditLimit:            'credit_limit',
  creditDays:             'credit_days',
  openingBalance:         'opening_balance',
  createdAt:              'created_at',
  updatedAt:              'updated_at'
};

function mapField(camel) {
  if (!camel || typeof camel !== 'string') return camel;
  return GRID_FIELD_TO_SQL[camel] || camel;
}

function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters
      .filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort
      .filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) {
    out.groupBy = mapField(out.groupBy);
  }
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: translateAdvancedRules(out.advancedFilters.rules)
    };
  }
  return out;
}

function translateAdvancedRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    // Nested groups carry their own `rules` array — recurse.
    if (Array.isArray(r.rules)) {
      return { ...r, rules: translateAdvancedRules(r.rules) };
    }
    if (typeof r.field === 'string' && r.field) {
      return { ...r, field: mapField(r.field) };
    }
    return r;
  });
}

export const customerApi = {
  list: (query) =>
    client.post(`${BASE}/list`, translateQuery(query) || {}).then(unwrap),
  get: (id) =>
    client.get(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  // Stage 6A.2I — optional approvalMfaChallengeId for the NIL-KYC
  // gate. Passed via the standard x-approval-mfa-challenge-id header
  // so the body still validates cleanly server-side. The header path
  // is exactly what the backend `requireApprovalMfa` middleware reads
  // (see middleware/requireApprovalMfa.js → readChallengeId).
  create: (payload, { approvalMfaChallengeId } = {}) =>
    client.post(BASE, payload, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  update: (id, payload, { approvalMfaChallengeId } = {}) =>
    client.put(`${BASE}/${encodeURIComponent(id)}`, payload, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  // Stage 6A.2D — `approvalMfaChallengeId` is optional in the type
  // signature but REQUIRED at runtime when the operation triggers
  // Approval MFA. Backend middleware re-rejects (401 APPROVAL_MFA_REQUIRED)
  // when missing, so callers using `useApprovalMfa()` automatically
  // launch the modal.
  updateStatus: (id, body, { approvalMfaChallengeId } = {}) =>
    client.patch(
      `${BASE}/${encodeURIComponent(id)}/status`,
      // When target status is 'active', the challenge id is harmless
      // (controller still sees it but middleware skipped the gate).
      approvalMfaChallengeId
        ? { ...body, approvalMfaChallengeId }
        : body
    ).then(unwrap),
  updateCell: (id, body) =>
    client.patch(`${BASE}/${encodeURIComponent(id)}/cell`, body).then(unwrap),
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${BASE}/${encodeURIComponent(id)}`, {
      // DELETE has no body, so we pass the challenge id via header.
      // The `requireApprovalMfa` middleware looks at both body and
      // x-approval-mfa-challenge-id header.
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),

  // Stage 6A.2B — GSTIN search backed by the tenant-scoped GSP
  // credential pool. Accepts "URP" too (returns { urp: true } so the
  // form skips GSTIN-related fields). Response shape:
  //   { status, customer:{...prefill}, raw:{...}, providerStatus?, warning? }
  //   | { urp: true }
  //   | { status:'local_valid', warning:'gsp_provider_not_configured', customer? }
  gstinSearch: (gstin) =>
    client.post(`${BASE}/gstin-search`, { gstin }).then(unwrap),

  // Stage 6A.2C — sales-rep dropdown lookup. Optional `q` filters by
  // name / username / email / mobile (server-side ILIKE). Returns up
  // to 100 active tenant users.
  listSalesRepresentatives: (q) =>
    client.get(`${BASE}/lookups/sales-representatives`, {
      params: q ? { q } : undefined
    }).then(unwrap),

  // HRMS salespersons (employee-based) for the customer "Salesperson"
  // field — distinct from the user-based Sales Representative above.
  // Source of truth is the HRMS employee master (active + is_salesperson).
  listSalespersons: (q) =>
    client.get('/api/erp/hrms/employees/salespersons', {
      params: { q: q || undefined, limit: 500 }
    }).then(unwrap),

  // Stage 6A.2E — parent customer dropdown lookup. Optional `q`
  // filters by code / name / display name (server-side ILIKE).
  // Optional `excludeId` removes the current row from the results
  // (Edit mode). Returns up to 50 active customers in this tenant.
  listParentCustomers: ({ q, excludeId } = {}) =>
    client.get(`${BASE}/lookups/parent-customers`, {
      params: {
        ...(q ? { q } : {}),
        ...(excludeId ? { excludeId } : {})
      }
    }).then(unwrap)
};

export default customerApi;
